import { Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { InfoHint } from "@/components/shared/info-hint";
import { KpiTile } from "@/components/shared/kpi-tile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabParam } from "@/hooks/use-tab-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { percent } from "@/lib/format";
import type { AggregateRiskEnvelope, AggregateRiskRow } from "@/lib/profitability-api";
import { EmptyBook, isEmptyBook, YourBook } from "./BookNotice";
import { ActionableMark, BillingTypeCell, RiskBasisBadge } from "./RiskBasisBadge";

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return <KpiTile label={label} value={value} help={hint} />;
}

function ProjectCell({ row }: { row: AggregateRiskRow }) {
  return (
    <td className="px-4 py-3 font-medium">
      <Link
        to="/dashboard/projects/$demandId"
        params={{ demandId: row.demand_id }}
        className="hover:underline"
      >
        {row.demand_name ?? row.demand_id}
      </Link>
      {row.actionable === null ? null : <ActionableMark actionable={row.actionable} />}
      {row.account_name ? (
        <span className="block text-xs font-normal text-muted-foreground">{row.account_name}</span>
      ) : null}
    </td>
  );
}

function burnLabel(row: AggregateRiskRow): string {
  if (!row.burn_trend || row.burn_trend === "unknown") return "not enough history";
  return row.burn_change_pct === null
    ? row.burn_trend
    : `${row.burn_trend} (${row.burn_change_pct > 0 ? "+" : ""}${row.burn_change_pct.toFixed(0)}%)`;
}

/** The engine's reason, rewritten in percentages, under the row it explains. */
function ReasonRow({ row, span }: { row: AggregateRiskRow; span: number }) {
  return (
    <tr className="border-b border-border last:border-0">
      <td colSpan={span} className="px-4 pb-3 pt-0 text-xs text-muted-foreground">
        {row.reason}
      </td>
    </tr>
  );
}

/**
 * At Risk for a Client Partner or CSM: their own book, hours exposure as a
 * percentage of budget only.
 *
 * A separate board, not the full one with columns hidden: the engine sends a
 * different row for this level. The dollars at risk, the cost rate behind
 * them, raw hours and the priced/unpriced split are all gone -- the split is
 * itself cost information (whether a rate exists) -- so ranked and unpriced
 * arrive as one list, ordered by exposure %.
 */
export function AggregateRiskBoard({ data }: { data: AggregateRiskEnvelope }) {
  const [tab, setTab] = useTabParam("ranked");

  if (isEmptyBook(data.access)) {
    return (
      <div className="w-full space-y-4">
        <EmptyBook access={data.access} />
      </div>
    );
  }

  const ranked = data.ranked;
  const perPeriod = data.per_period_budget;
  const notAssessable = data.not_assessable;
  const columns = 7;

  return (
    <div className="w-full space-y-4">
      <YourBook
        access={data.access}
        count={ranked.length + perPeriod.length + notAssessable.length}
        noun={["project", "projects"]}
        scope="on this board"
      />
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label="At risk"
          value={String(ranked.length)}
          hint="Over budget, projected over, or working past the end date"
        />
        <Stat
          label="Per-period budget"
          value={String(perPeriod.length)}
          hint="Budget is a monthly allowance, compared month by month"
        />
        <Stat
          label="Not assessable"
          value={String(notAssessable.length)}
          hint="No budget and no end date to measure against"
        />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center gap-1">
          <TabsList>
            <TabsTrigger value="ranked">At risk ({ranked.length})</TabsTrigger>
            <TabsTrigger value="per-period">Per-period budget ({perPeriod.length})</TabsTrigger>
            <TabsTrigger value="unassessable">Not assessable ({notAssessable.length})</TabsTrigger>
          </TabsList>

          <InfoHint text={data.ranking_rule} data-ranking-rule />
        </div>

        <TabsContent value="ranked" className="space-y-3">
          {ranked.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="Nothing at risk on your accounts"
              description="No project in your book is over its hours budget, projected to go over, or working past its end date."
            />
          ) : (
            <DataTable
              headers={[
                { label: "Project", help: COLUMN_HELP.riskProject },
                { label: "What this is", help: COLUMN_HELP.riskBasis },
                { label: "Contract", help: COLUMN_HELP.riskContract },
                {
                  label: "Exposure",
                  className: "text-right",
                  help: "How far past its hours budget the project is, or is projected to be, as a percentage of that budget.",
                },
                {
                  label: "Budget used",
                  className: "text-right",
                  help: COLUMN_HELP.budgetConsumed,
                },
                { label: "Calendar", className: "text-right", help: COLUMN_HELP.calendarElapsed },
                { label: "Burn", className: "text-right", help: COLUMN_HELP.burnTrend },
              ]}
            >
              {ranked.flatMap((row) => [
                <tr key={row.demand_id} className="hover:bg-muted/30">
                  <ProjectCell row={row} />
                  <td className="px-4 py-3">
                    {row.exposure_basis ? <RiskBasisBadge basis={row.exposure_basis} /> : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <BillingTypeCell billingType={row.billing_type} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {percent(row.exposure_pct_of_budget)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {percent(row.budget_consumed_pct)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {percent(row.calendar_elapsed_pct)}
                  </td>
                  <td className="px-4 py-3 text-right text-xs">{burnLabel(row)}</td>
                </tr>,
                <ReasonRow key={`${row.demand_id}-reason`} row={row} span={columns} />,
              ])}
            </DataTable>
          )}
        </TabsContent>

        <TabsContent value="per-period" className="space-y-3">
          {perPeriod.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No monthly-allowance projects"
              description="None of your projects is budgeted as a monthly allowance."
            />
          ) : (
            <DataTable
              headers={[
                { label: "Project", help: COLUMN_HELP.riskProject },
                { label: "Contract", help: COLUMN_HELP.riskContract },
                {
                  label: "Vs monthly budget",
                  className: "text-right",
                  help: "Average month against the monthly allowance, as a percentage over (+) or under (-).",
                },
                { label: "Calendar", className: "text-right", help: COLUMN_HELP.calendarElapsed },
                { label: "Burn", className: "text-right", help: COLUMN_HELP.burnTrend },
              ]}
            >
              {perPeriod.flatMap((row) => [
                <tr key={row.demand_id} className="hover:bg-muted/30">
                  <ProjectCell row={row} />
                  <td className="px-4 py-3">
                    <BillingTypeCell billingType={row.billing_type} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {percent(row.vs_period_budget_pct)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {percent(row.calendar_elapsed_pct)}
                  </td>
                  <td className="px-4 py-3 text-right text-xs">{burnLabel(row)}</td>
                </tr>,
                <ReasonRow key={`${row.demand_id}-reason`} row={row} span={5} />,
              ])}
            </DataTable>
          )}
        </TabsContent>

        <TabsContent value="unassessable" className="space-y-3">
          {notAssessable.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="Every project could be assessed"
              description="All of your projects have a budget or an end date to measure against."
            />
          ) : (
            <DataTable
              headers={[
                { label: "Project", help: COLUMN_HELP.riskProject },
                { label: "Contract", help: COLUMN_HELP.riskContract },
                { label: "Ends", help: "The scheduled end date, when one is set." },
              ]}
            >
              {notAssessable.flatMap((row) => [
                <tr key={row.demand_id} className="hover:bg-muted/30">
                  <ProjectCell row={row} />
                  <td className="px-4 py-3">
                    <BillingTypeCell billingType={row.billing_type} />
                  </td>
                  <td className="px-4 py-3 text-sm">{row.end_date ?? "No end date"}</td>
                </tr>,
                <ReasonRow key={`${row.demand_id}-reason`} row={row} span={3} />,
              ])}
            </DataTable>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
